import React from "react";
import { Box, Typography, Paper, alpha, useTheme, Chip } from "@mui/material";
import { LocalOffer, CalendarToday, Category } from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";

const OfferInfoSection = ({ offerDetails }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: "white",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <LocalOffer sx={{ color: theme.palette.error.main, fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: theme.palette.text.primary,
          }}
        >
          Offer Details
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Offer Image */}
        {offerDetails.image && (
          <Box
            sx={{
              width: "100%",
              height: 200,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: alpha(theme.palette.error.main, 0.05),
              mb: 1,
            }}
          >
            <ImageLoader
              imagePath={offerDetails.image}
              width="100%"
              height={200}
              alt={offerDetails.name}
              borderRadius={2}
            />
          </Box>
        )}

        {/* Type and Status */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={offerDetails.type.toUpperCase()}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          />
          <Chip
            label={offerDetails.isActive ? "ACTIVE" : "INACTIVE"}
            size="small"
            sx={{
              bgcolor: offerDetails.isActive
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.grey[500], 0.1),
              color: offerDetails.isActive
                ? theme.palette.success.main
                : theme.palette.grey[600],
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Category & Subcategory */}
        {(offerDetails.Category || offerDetails.SubCategory) && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 1.5,
            }}
          >
            {offerDetails.Category && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Category
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.65rem",
                      display: "block",
                    }}
                  >
                    Category
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                  >
                    {offerDetails.Category.name}
                  </Typography>
                </Box>
              </Box>
            )}
            {offerDetails.SubCategory && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Category
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.65rem",
                      display: "block",
                    }}
                  >
                    Subcategory
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                  >
                    {offerDetails.SubCategory.name}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Description */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
              textTransform: "uppercase",
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            Description
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontSize: "0.9rem",
              mt: 0.5,
              lineHeight: 1.6,
            }}
          >
            {offerDetails.description || "No description available"}
          </Typography>
        </Box>

        {/* Dates */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            pt: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              Valid From
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <CalendarToday
                sx={{ fontSize: 14, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.85rem",
                }}
              >
                {formatDate(offerDetails.startDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              Valid Until
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <CalendarToday
                sx={{ fontSize: 14, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.85rem",
                }}
              >
                {formatDate(offerDetails.endDate)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Timestamps */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.65rem",
                textTransform: "uppercase",
              }}
            >
              Created
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.3 }}>
              {formatDateTime(offerDetails.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.65rem",
                textTransform: "uppercase",
              }}
            >
              Last Updated
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.3 }}>
              {formatDateTime(offerDetails.updatedAt)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default OfferInfoSection;
